import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { CommonModule } from '@angular/common'
import { PipesModule } from '@shared/pipe/index.module'
import { ScopeByRoleTaskComponent } from '@app/features/scope-overview/components/scope-tab/scope-by-role-task/scope-by-role-task.component'
import { trackById } from '@shared/utils/utils'
import { User } from '@core/model/user.model'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { ScopeVersion } from '@core/model/scope-version'
import { Sort } from '@angular/material/sort'
import { CdnConfig } from '@core/model/cdn-config.model'
import { MatIconModule } from '@angular/material/icon'
import { ScopeSection } from '@app/features/scope-overview/model/scope-section'
import { ExchangeRateService } from '@shared/services/exchange-rate.service'
import { MatFormFieldModule } from '@angular/material/form-field'
import { ScopeUiDuration } from '@shared/components/ui-components/scope-ui-duration/scope-ui-duration.component'
import { SharedModule } from '@shared/shared.module'
import { ScopeOverviewActions } from '@app/features/scope-overview/store/actions/scope-overview.action'
import { plainToInstance } from 'class-transformer'
import { Store } from '@ngrx/store'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { ModalConfig } from '@core/model/modal-config.model'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { LanguageService } from '@core/service/language.service'
import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { ScopeTabService } from '@app/features/scope-overview/service/scope-tab.service'

@Component({
  selector: 'scope-by-role-stage',
  standalone: true,
  imports: [
    CommonModule,
    PipesModule,
    ScopeByRoleTaskComponent,
    MatIconModule,
    MatFormFieldModule,
    ScopeUiDuration,
    SharedModule,
  ],
  templateUrl: './scope-by-role-stage.component.html',
  styleUrls: ['./scope-by-role-stage.component.scss'],
})
export class ScopeByRoleStageComponent implements OnInit {
  @Input() currentUser!: User
  @Input() currentRatecard!: RatecardVersion
  @Input() set sortedDefaultSectionDeliverables(deliverables: Deliverable[]) {
    this.sortedSectionDeliverables = deliverables.sort((a, b) => a.order - b.order)
  }
  @Input() sectionDrops: string[]
  @Input() sectionInDrag: boolean
  @Input() set scopeVersion(scope: ScopeVersion) {
    this.currentScope = scope
    if (this.currentScope && !this.stageActions) {
      this.stageActions = [
        {
          name: () => 'View and edit',
          icon: 'border_color',
          click: () => {
            this.editScopeSection()
          },
          isVisible: () => {
            return this.scopeApprovalFlowService.isScopeEditable(this.currentScope)
          },
        },
        {
          name: () => `Delete`,
          icon: 'delete',
          click: () => this.deleteSection(),
          showDivider: true,
          isVisible: () => {
            return this.scopeApprovalFlowService.isScopeEditable(this.currentScope)
          },
        },
      ]
    }
  }
  @Input() scopeSection!: ScopeSection
  @Input() sectionStates!: any
  @Input() tableExpanded!: boolean
  @Input() departmentSort!: Sort
  @Input() cdnConfig?: CdnConfig
  @Output() tableExpandedChange = new EventEmitter<boolean>()
  @Output() deleteTask = new EventEmitter<Deliverable>()
  @Output() onAcceptDeliverable = new EventEmitter<Deliverable>()
  @Output() onRejectDeliverable = new EventEmitter<Deliverable>()
  @Output() onDeleteScopeSection = new EventEmitter<any>()
  @Output() onDropDeliverable: EventEmitter<{ event: CdkDragDrop<any>; section: ScopeSection | null }>
  currentScope: ScopeVersion
  mouseover = false
  edit = false
  stageActions: any[]
  sortedSectionDeliverables: Deliverable[] = []

  constructor(
    public exchangeRateService: ExchangeRateService,
    private store: Store,
    public scopeApprovalFlowService: ApprovalFlowService,
    private dialog: MatDialog,
    private lang: LanguageService,
    private cdr: ChangeDetectorRef,
    public scopeTabService: ScopeTabService
  ) {
    this.onDropDeliverable = new EventEmitter<{ event: CdkDragDrop<any>; section: ScopeSection | null }>()
  }

  deleteDeliverable(event) {
    this.deleteTask.emit(event)
  }

  deleteSection = () => {
    this.onDeleteScopeSection.emit(this.scopeSection)
  }

  openStage() {
    this.sectionStates[this.scopeSection.id] = !this.sectionStates[this.scopeSection.id]
  }

  ngOnInit() {
    this.sectionStates[this.scopeSection.id] = false
  }

  getNameTooLong() {
    return this.scopeSection.name.length > 50
  }

  acceptDeliverable(event) {
    this.onAcceptDeliverable.emit(event)
  }

  rejectDeliverable(event) {
    this.onRejectDeliverable.emit(event)
  }

  tableExpandedChanged(event) {
    this.tableExpandedChange.emit(event)
  }

  dropDeliverable(event: CdkDragDrop<any>, section: ScopeSection | null) {
    this.onDropDeliverable.emit({ event, section })
    this.scopeTabService.deliverableDragging = false
  }

  onTaskDragStart() {
    this.scopeTabService.deliverableDragging = true
    Object.keys(this.scopeTabService.taskOpenStates).forEach((key) => {
      this.scopeTabService.taskOpenStates[key] = false
    })
    Object.keys(this.sectionStates).forEach((key) => {
      this.sectionStates[key] = true
    })
    this.cdr.detectChanges()
  }

  editScopeSection() {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Edit ${this.lang.get('stage')}`,
        undefined,
        'Save',
        undefined,
        (form: FormControl) => {
          dialog.close()
          const scopeSection = new ScopeSection()
          scopeSection.name = form.get('name')?.value
          scopeSection.description = form.get('description')?.value
          scopeSection.startDate = form.get('duration')?.value?.startDate
          scopeSection.endDate = form.get('duration')?.value?.endDate
          this.store.dispatch(
            ScopeOverviewActions.updateScopeSection({
              scopeId: this.currentScope.identity.id,
              section: plainToInstance(ScopeSection, {
                ...this.scopeSection,
                name: scopeSection.name,
                description: scopeSection.description,
                startDate: scopeSection.startDate,
                endDate: scopeSection.endDate,
              }),
            })
          )
        },
        undefined,
        [
          { name: 'name', control: new FormControl(this.scopeSection.name), type: 'text', label: 'Name' },
          { name: 'description', control: new FormControl(this.scopeSection.description), type: 'description' },
          {
            name: 'duration',
            control: new FormControl({ startDate: this.scopeSection.startDate, endDate: this.scopeSection.endDate }),
            type: 'duration',
            minDate: this.currentScope.startDate,
            maxDate: this.currentScope.endDate,
          },
        ],
        undefined,
        undefined,
        undefined,
        (form: FormControl) => form.value.name
      ),
    })
  }

  protected readonly trackById = trackById
}
